import { useState } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { Trash2, Flag, MoreHorizontal, Ban } from 'lucide-react'
import { cn } from '../lib/utils'
import { UserAvatar } from './UserAvatar'
import { MediaLinkCard } from './MediaLinkCard'
import { ConfirmDialog } from './ConfirmDialog'
import { ReportDialog } from './ReportDialog'
import { formatRelativeTime } from '../lib/formatDate'
import { unknownMemberName, blockUserLabel, blockUserConfirmTitle, blockConfirmLabel } from '../lib/commonStrings'
import { useBlockedUsers } from '../contexts/BlockedUsersContext'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import type { SetPost, UserProfile } from '../types'
import { clickableProps } from '../lib/a11y'

interface Props {
  post: SetPost
  profile: UserProfile | undefined
  isAuthor: boolean
  onDelete: () => void
  contextLabel?: string
  onClick?: () => void
}

export function SetPostItem({ post, profile, isAuthor, onDelete, contextLabel, onClick }: Props) {
  const { locale } = useLocaleContext()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false)
  const { blockUser } = useBlockedUsers()
  const name = profile?.displayName?.trim() || unknownMemberName()
  const created = post.createdAt?.toDate?.()

  async function handleBlock() {
    setBlockConfirmOpen(false)
    await blockUser(post.authorId)
    toast.success(fbt('User blocked', 'Toast after blocking a user from a post'))
  }

  return (
    <li className={cn('flex gap-2.5', onClick && 'cursor-pointer')} {...clickableProps(onClick)}>
      <UserAvatar profile={profile} fallbackName={name} size="sm" />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate">{name}</span>
          {created && (
            <span className="text-[11px] text-muted-foreground shrink-0">
              {formatRelativeTime(created, locale)}
            </span>
          )}
          {contextLabel && (
            <span className="ml-auto shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary max-w-[40%] truncate">
              {contextLabel}
            </span>
          )}
          {isAuthor ? (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setConfirmOpen(true) }}
              className={cn('shrink-0 text-muted-foreground hover:text-destructive cursor-pointer', !contextLabel && 'ml-auto')}
              aria-label={fbt('Delete post', 'Accessible label for the delete-post button in the set feed')}
            >
              <Trash2 className="size-3.5" />
            </button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={e => e.stopPropagation()}
                  className={cn('shrink-0 text-muted-foreground hover:text-foreground cursor-pointer', !contextLabel && 'ml-auto')}
                  aria-label={fbt('Post actions', 'Accessible label for the post actions menu')}
                >
                  <MoreHorizontal className="size-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={e => { e.stopPropagation(); setReportOpen(true) }}>
                  <Flag className="size-4" />
                  {fbt('Report post', 'Menu item to report a post in the set feed')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={e => { e.stopPropagation(); setBlockConfirmOpen(true) }}
                >
                  <Ban className="size-4" />
                  {blockUserLabel()}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {post.text && <p className="text-sm whitespace-pre-wrap break-words">{post.text}</p>}
        {post.mediaUrl && <MediaLinkCard url={post.mediaUrl} />}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={onDelete}
        title={fbt('Delete this post?', 'Confirm dialog title for deleting a set feed post')}
        description={fbt(
          'This removes it from the set feed for everyone who can see it.',
          'Confirm dialog body for deleting a set feed post',
        )}
        confirmLabel={fbt('Delete', 'Confirm button to delete a set feed post')}
        destructive
      />
      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        contentType="post"
        contentId={post.id}
        reportedUserId={post.authorId}
      />
      <ConfirmDialog
        open={blockConfirmOpen}
        onOpenChange={setBlockConfirmOpen}
        onConfirm={handleBlock}
        title={blockUserConfirmTitle()}
        description={fbt(
          "You won't see their posts anymore. You can unblock them later from their profile.",
          'Confirm dialog body for blocking a user from a post',
        )}
        confirmLabel={blockConfirmLabel()}
        destructive
      />
    </li>
  )
}
